import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import PublicLayout from '@/components/hydlib/PublicLayout';
import { useAuth } from '@/context/AuthContext';

export default function SignUp() {
    const { signup } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const rentMode = searchParams.get('mode') === 'rent';
    const eventsMode = searchParams.get('mode') === 'events';
    const freeMode = rentMode || eventsMode;
    const redirect = searchParams.get('redirect') || (eventsMode ? '/events' : '/catalog');

    const [period, setPeriod] = useState('monthly');
    const [adultQty, setAdultQty] = useState(1);
    const [childQty, setChildQty] = useState(0);
    const [childName, setChildName] = useState('');
    const [form, setForm] = useState({ name: '', phone: '', email: '', address: '', password: '', confirm: '' });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: k === 'phone' ? e.target.value.replace(/\D/g, '') : e.target.value });

    const validate = () => {
        if (!form.name.trim()) return 'Please enter your full name';
        if (form.phone.length !== 10) return 'Phone must be exactly 10 digits';
        if (!form.email.includes('@')) return 'Please enter a valid email';
        if (form.password.length < 6) return 'Password must be at least 6 characters';
        if (form.password !== form.confirm) return "Passwords don't match";
        if (!freeMode && adultQty + childQty < 1) return 'Please select at least one membership';
        if (!freeMode && childQty > 0 && !childName.trim()) return "Please enter your child's name";
        return null;
    };

    const scrollToPay = (e) => {
        e.preventDefault();
        const v = validate();
        if (v) { setError(v); return; }
        setError('');
        document.getElementById('signup-pay')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const handleRentSignup = async (e) => {
        e.preventDefault();
        const v = validate();
        if (v) { setError(v); return; }
        setError('');
        setLoading(true);
        const result = await signup(form.email, form.password, form.name, form.phone);
        setLoading(false);
        if (!result.success) {
            setError(result.error || 'Signup failed');
            return;
        }
        navigate(redirect);
    };

    const handlePay = async () => {
        const v = validate();
        if (v) { setError(v); return; }
        setError('');
        setLoading(true);
        const result = await signup(form.email, form.password, form.name, form.phone);
        setLoading(false);
        if (!result.success) {
            setError(result.error || 'Signup failed');
            return;
        }
        // Send them straight to /payments with the chosen bundle preselected.
        const childParam = childQty > 0 && childName.trim() ? `&child_name=${encodeURIComponent(childName.trim())}` : '';
        navigate(`/my-library?plan=bundle_${period}&adults=${adultQty}&children=${childQty}${childParam}`);
    };

    const PRICES = { monthly: { adult: 500, child: 300, unit: '/month' }, annual: { adult: 5000, child: 3000, unit: '/year' } };
    const total = adultQty * PRICES[period].adult + childQty * PRICES[period].child;
    const fmt = (n) => `₹${n.toLocaleString('en-IN')}`;
    const planLabel = [adultQty > 0 ? `${adultQty} adult` : null, childQty > 0 ? `${childQty} children's` : null].filter(Boolean).join(' + ') + ` (${period})`;
    const payLabel = `Pay ${fmt(total)} → start reading`;

    const stepBtn = { cursor: 'pointer', width: 30, height: 30, border: '1px solid var(--line)', background: 'var(--bg)', color: 'var(--ink)', fontSize: 17, lineHeight: 1, fontFamily: 'inherit' };
    const Stepper = ({ value, onChange, testid }) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <button type="button" onClick={() => onChange(Math.max(0, value - 1))} data-testid={`${testid}-minus`} style={stepBtn}>−</button>
            <span data-testid={`${testid}-qty`} style={{ fontFamily: "'Space Mono', monospace", fontSize: 17, minWidth: 20, textAlign: 'center' }}>{value}</span>
            <button type="button" onClick={() => onChange(Math.min(10, value + 1))} data-testid={`${testid}-plus`} style={stepBtn}>+</button>
        </span>
    );

    const inputStyle = { border: '1px solid var(--line)', background: 'var(--bg)', padding: '11px 13px', fontSize: 15, color: 'var(--ink)', fontFamily: 'inherit' };
    const labelStyle = { display: 'flex', flexDirection: 'column', gap: 6, fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase' };

    return (
        <PublicLayout theme="archive" grid="--a5" gridStrength={30}>
            <div className="hydlib-scallop" style={{ backgroundImage: 'radial-gradient(circle at 50% 0, var(--a5) 0 5px, transparent 6px)' }} />
            <div className="hydlib-pad" style={{ maxWidth: 1080, margin: '0 auto', padding: '56px 48px 72px' }}>
                <h1 style={{ fontFamily: "'Libre Caslon Display', serif", fontWeight: 400, fontSize: 48, margin: '0 0 8px' }}>
                    <span style={{ background: 'var(--bg)', boxShadow: '8px 0 0 var(--bg), -8px 0 0 var(--bg)' }}>{rentMode ? 'Sign up to rent' : eventsMode ? 'Sign up free' : 'Become a member'}</span>
                </h1>
                <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a1)', margin: '0 0 44px' }}>
                    <span style={{ background: 'var(--bg)', boxShadow: '6px 0 0 var(--bg), -6px 0 0 var(--bg)' }}>{rentMode ? 'No membership needed — ₹200 per adult book · ₹100 per children\u2019s' : eventsMode ? 'No payment needed — reserve event spots in seconds' : 'Three steps and you\u2019re reading by the weekend'}</span>
                </p>

                <div className="hydlib-cols" style={{ display: 'grid', gridTemplateColumns: freeMode ? '1fr' : '1fr 1.1fr', gap: 40, alignItems: 'start', maxWidth: freeMode ? 640 : 'none' }}>
                    {/* STEP 1 · PLAN */}
                    {!freeMode && (
                    <div style={{ position: 'relative', border: '1px solid var(--line)', borderTop: '6px solid var(--a5)', background: 'var(--paper)', padding: '32px 30px' }}>
                        <div style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a5)', marginBottom: 14 }}>Step 1 · Build your plan</div>
                        <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
                            {['monthly', 'annual'].map((p) => (
                                <button key={p} type="button" onClick={() => setPeriod(p)} data-testid={`period-${p}`}
                                    style={{ cursor: 'pointer', flex: 1, position: 'relative', border: `2px solid ${period === p ? 'var(--a2)' : 'var(--line)'}`, background: 'var(--bg)', padding: '12px 10px', color: 'var(--ink)', fontFamily: "'Space Mono', monospace", fontSize: 13, letterSpacing: 1, textTransform: 'uppercase' }}>
                                    {period === p ? '●' : '○'} {p}
                                    {p === 'annual' && <span style={{ position: 'absolute', top: -11, right: 8, background: 'var(--a3)', color: 'var(--o3)', fontSize: 10, letterSpacing: 1, padding: '2px 8px', border: '1px solid var(--line)' }}>BEST VALUE</span>}
                                </button>
                            ))}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid var(--line)', background: 'var(--bg)', padding: '14px 16px', marginBottom: 12 }}>
                            <span>
                                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 13, letterSpacing: 1 }}>ADULT</span>
                                <span style={{ display: 'block', fontSize: 13, opacity: 0.75 }}>{fmt(PRICES[period].adult)}{PRICES[period].unit} each · all books</span>
                            </span>
                            <Stepper value={adultQty} onChange={setAdultQty} testid="adult" />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid var(--line)', background: 'var(--bg)', padding: '14px 16px' }}>
                            <span>
                                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 13, letterSpacing: 1 }}>CHILD</span>
                                <span style={{ display: 'block', fontSize: 13, opacity: 0.75 }}>{fmt(PRICES[period].child)}{PRICES[period].unit} each · kids&apos; books only</span>
                            </span>
                            <Stepper value={childQty} onChange={setChildQty} testid="child" />
                        </div>
                        {childQty > 0 && (
                            <label style={{ ...labelStyle, marginTop: 12 }}>Child&apos;s name{childQty > 1 ? 's' : ''}
                                <input type="text" value={childName} onChange={(e) => setChildName(e.target.value)} placeholder={childQty > 1 ? 'Aarav, Diya' : 'Aarav'} data-testid="signup-child-name" style={inputStyle} />
                            </label>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderTop: '1px dashed var(--line)', marginTop: 16, paddingTop: 14 }} data-testid="signup-total">
                            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: 2, textTransform: 'uppercase' }}>Total · one payment</span>
                            <span style={{ fontFamily: "'Libre Caslon Display', serif", fontSize: 30 }}>{fmt(total)}<span style={{ fontSize: 14, fontFamily: "'Libre Caslon Text', serif" }}>{PRICES[period].unit}</span></span>
                        </div>
                        <div style={{ fontSize: 14.5, lineHeight: 2, marginTop: 10 }}>
                            ✓ Five books at a time, per membership<br />
                            ✓ 21-day loans, free extensions<br />
                            ✓ All memberships renew together
                        </div>
                        <span style={{ position: 'absolute', bottom: -18, right: 26, fontFamily: "'Caveat', cursive", fontSize: 21, fontWeight: 600, color: 'var(--a1)', transform: 'rotate(-3deg)', background: 'var(--paper)', padding: '0 8px' }}>worth it, promise!</span>
                    </div>
                    )}

                    {/* STEP 2 · DETAILS */}
                    <div style={{ position: 'relative', border: '1px solid var(--line)', borderTop: '6px solid var(--a1)', background: 'var(--paper)', padding: '32px 30px' }}>
                        <div style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a1)', marginBottom: 14 }}>{freeMode ? 'Tell us who you are' : 'Step 2 · Tell us who you are'}</div>
                        <form onSubmit={freeMode ? handleRentSignup : scrollToPay} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }} className="hydlib-cols">
                            <label style={labelStyle}>Full name
                                <input type="text" value={form.name} onChange={set('name')} placeholder="Meera Rao" required data-testid="signup-name" style={inputStyle} />
                            </label>
                            <label style={labelStyle}>Phone
                                <input type="tel" value={form.phone} onChange={set('phone')} placeholder="98XXXXXXXX" required pattern="[0-9]{10}" maxLength={10} data-testid="signup-phone" style={inputStyle} />
                            </label>
                            <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>Email
                                <input type="email" value={form.email} onChange={set('email')} placeholder="you@example.com" required data-testid="signup-email" style={inputStyle} />
                            </label>
                            <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>Delivery address <span style={{ textTransform: 'none', letterSpacing: 0, fontFamily: "'Libre Caslon Text', serif", fontStyle: 'italic', opacity: 0.7 }}>(optional — for Thursday deliveries)</span>
                                <input type="text" value={form.address} onChange={set('address')} placeholder="Flat, street, area, pincode" data-testid="signup-address" style={inputStyle} />
                            </label>
                            <label style={labelStyle}>Password
                                <input type="password" value={form.password} onChange={set('password')} placeholder="••••••••" required data-testid="signup-password" style={inputStyle} />
                            </label>
                            <label style={labelStyle}>Confirm password
                                <input type="password" value={form.confirm} onChange={set('confirm')} placeholder="••••••••" required data-testid="signup-confirm" style={inputStyle} />
                            </label>
                            {error && <div style={{ gridColumn: '1 / -1', color: 'var(--a2)', fontSize: 14, padding: '6px 10px', border: '1px solid var(--a2)', background: 'color-mix(in oklch, var(--a2) 10%, transparent)' }} data-testid="signup-error">{error}</div>}
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="signup-continue"
                                className="hydlib-highlight"
                                style={{ gridColumn: '1 / -1', cursor: loading ? 'wait' : 'pointer', border: 'none', marginTop: 6, fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 14, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--ink)', padding: 13, background: `linear-gradient(104deg, transparent 1.5%, color-mix(in oklch, var(--a1) 45%, var(--paper)) 5%, color-mix(in oklch, var(--a1) 30%, var(--paper)) 52%, color-mix(in oklch, var(--a1) 50%, var(--paper)) 94%, transparent 98.5%)`, opacity: loading ? 0.7 : 1 }}
                            >
                                {rentMode ? (loading ? 'Creating account…' : 'Create account → start renting') : eventsMode ? (loading ? 'Creating account…' : 'Create free account → reserve spots') : 'Continue to payment ↓'}
                            </button>
                        </form>
                        {freeMode && (
                            <p style={{ fontSize: 14.5, marginTop: 18, marginBottom: 0 }}>
                                Want unlimited books instead?{' '}
                                <Link to="/signup" style={{ color: 'var(--a3)', textDecoration: 'underline wavy 2px var(--a1)', textUnderlineOffset: 4 }} data-testid="switch-to-member">Become a member — ₹500/month →</Link>
                            </p>
                        )}
                    </div>
                </div>

                {/* STEP 3 · PAY */}
                {!freeMode && (
                <div id="signup-pay" style={{ position: 'relative', border: '1px solid var(--line)', borderTop: '6px solid var(--a2)', background: 'var(--paper)', padding: '28px 30px', marginTop: 40, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 18 }}>
                    <div>
                        <div style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a2)', marginBottom: 6 }}>Step 3 · Pay &amp; start reading</div>
                        <div style={{ fontSize: 15 }}>You&apos;re signing up for the <strong>{planLabel}</strong> plan. Pay via UPI or card — your first five books are one browse away.</div>
                    </div>
                    <button
                        onClick={handlePay}
                        disabled={loading}
                        data-testid="signup-pay-cta"
                        className="hydlib-highlight"
                        style={{ cursor: loading ? 'wait' : 'pointer', border: 'none', fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 15, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--ink)', padding: '14px 28px', background: `linear-gradient(104deg, transparent 1.5%, color-mix(in oklch, var(--a2) 62%, var(--paper)) 5%, color-mix(in oklch, var(--a2) 46%, var(--paper)) 52%, color-mix(in oklch, var(--a2) 68%, var(--paper)) 94%, transparent 98.5%)`, opacity: loading ? 0.7 : 1 }}
                    >
                        {loading ? 'Creating account…' : payLabel}
                    </button>
                </div>
                )}

                {!freeMode && (
                    <p style={{ fontSize: 15, marginTop: 20 }} data-testid="events-free-signup-hint">
                        Just here for Sunday events?{' '}
                        <Link to="/signup?mode=events" style={{ color: 'var(--a3)', textDecoration: 'underline wavy 2px var(--a1)', textUnderlineOffset: 4 }} data-testid="events-free-signup-link">
                            Create a free account — no payment needed →
                        </Link>
                    </p>
                )}
            </div>
        </PublicLayout>
    );
}
